import { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getStockMovements } from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import DataTable from "../components/UI/DataTable";
import PageHeader from "../components/UI/PageHeader";
import { useTranslation } from "../context/I18nContext";

export default function StockMovements() {
  const { t } = useTranslation("stock_movements");

  const TYPE_LABELS = {
    purchase: t("type_purchase", "Purchase"),
    sale: t("type_sale", "Sale"),
    adjustment: t("type_adjustment", "Adjustment"),
    transfer_in: t("type_transfer_in", "Transfer In"),
    transfer_out: t("type_transfer_out", "Transfer Out"),
    return: t("type_return", "Return"),
  };

  const [movements, setMovements] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: "created_at", direction: "desc" });
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const branchId = searchParams.get("branch_id") || "";
  const productId = searchParams.get("product_id") || "";

  useEffect(() => {
    const params = {};
    if (branchId) params.branch_id = branchId;
    if (productId) params.product_id = productId;

    getStockMovements(token, Object.keys(params).length ? params : undefined)
      .then((data) => setMovements(data || []))
      .catch(() => setMovements([]));
  }, [branchId, productId, token]);

  // Only date/quantity are plain sortable primitives on the row — product,
  // branch, and type are nested objects/labels, so sorting by them here
  // wouldn't do anything meaningful; requestSort still works for the two
  // columns that matter.
  const sortedMovements = useMemo(() => {
    const result = [...movements];
    result.sort((a, b) => {
      let valA = a[sortConfig.key];
      let valB = b[sortConfig.key];
      if (typeof valA === "string") valA = valA.toLowerCase();
      if (typeof valB === "string") valB = valB.toLowerCase();
      if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
    return result;
  }, [movements, sortConfig]);

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
  };

  const columns = [
    {
      key: "created_at",
      label: t("col_date", "Date"),
      className: "text-gray-500",
      render: (row) => new Date(row.created_at).toLocaleString(),
    },
    { key: "product", label: t("col_product", "Product"), render: (row) => row.product?.name || `#${row.product_id}` },
    { key: "branch", label: t("col_branch", "Branch"), className: "text-gray-600", render: (row) => row.branch?.name || `#${row.branch_id}` },
    {
      key: "type",
      label: t("col_type", "Type"),
      render: (row) => (
        <span className="px-2 py-1 rounded-full text-[10px] font-bold uppercase bg-gray-100 text-gray-600">
          {TYPE_LABELS[row.type] || row.type}
        </span>
      ),
    },
    {
      key: "quantity",
      label: t("col_quantity", "Quantity"),
      render: (row) => (
        <span className={`font-mono font-bold ${row.quantity >= 0 ? "text-green-600" : "text-red-500"}`}>
          {row.quantity >= 0 ? "+" : ""}{row.quantity}
        </span>
      ),
    },
    { key: "reason", label: t("col_reason", "Reason"), className: "text-gray-500 italic", render: (row) => row.reason || "—" },
  ];

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto transition-all duration-300">
      <PageHeader className="flex items-center gap-4 mb-8 border-b border-gray-100 pb-6">
        <button onClick={() => navigate("/inventory")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
          <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
        </button>
        <div className="flex items-center justify-center min-w-[48px] h-[48px] text-[var(--primary-color)]">
          <ICONS.StockMovements size={40} className="shrink-0" />
        </div>
        <div>
          <h2 className="text-xl md:text-2xl font-bold" style={{ color: "var(--primary-color)" }}>{t("page_title", "Stock Movement History")}</h2>
          <p className="text-gray-400 text-xs md:text-sm font-medium">
            {productId || branchId ? t("page_subtitle_filtered", "Filtered to the selected product/branch — most recent first") : t("page_subtitle_all", "Every inventory change across all branches — most recent first")}
          </p>
        </div>
      </PageHeader>

      <DataTable
        columns={columns}
        data={sortedMovements}
        sortConfig={sortConfig}
        onSort={requestSort}
        renderActions={() => null}
        mobileRender={(row) => (
          <div className="flex justify-between items-center">
            <span className="text-[10px] text-gray-400">{new Date(row.created_at).toLocaleDateString()}</span>
            <h3 className="font-bold text-gray-800">{row.product?.name}</h3>
          </div>
        )}
      />
    </div>
  );
}
